import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { Guide } from "@ckgo2/shared/types";
import { Plus, Search, User, Edit, Trash2, Star, Languages } from "lucide-react";

export default function GuidesPage() {
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["guides"],
    queryFn: () => api.get<ApiResponse<Guide[]>>("/api/guides"),
  });

  const guides = data?.data || [];

  // Filter guides
  const filteredGuides = guides.filter((guide) => {
    const matchesSearch =
      guide.name.toLowerCase().includes(search.toLowerCase()) ||
      guide.bio?.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  if (error) {
    return (
      <div className="card text-center text-error-600">
        Chyba při načítání průvodců. Zkuste to prosím znovu.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Průvodci
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Spravujte průvodce zájezdů
          </p>
        </div>
        <Link to="/guides/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          Nový průvodce
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Hledat průvodce..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        </div>
      ) : filteredGuides.length === 0 ? (
        <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
          <User className="mb-2 h-12 w-12 text-gray-300" />
          <p>Žádní průvodci nenalezeni</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredGuides.map((guide) => (
            <div
              key={guide.id}
              className="card overflow-hidden p-0"
            >
              {/* Photo */}
              <div className="aspect-[3/2] bg-gray-100 dark:bg-gray-800">
                {guide.photo ? (
                  <img
                    src={guide.photo}
                    alt={guide.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <User className="h-16 w-16 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {guide.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      /{guide.slug}
                    </p>
                  </div>
                  {guide.isActive && (
                    <span className="badge-primary flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      Active
                    </span>
                  )}
                </div>

                {/* Specializations */}
                {guide.specializations && guide.specializations.length > 0 && (
                  <div className="mt-3 flex items-center gap-2">
                    <Languages className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {guide.specializations.join(", ")}
                    </span>
                  </div>
                )}

                {/* Bio preview */}
                {guide.bio && (
                  <p className="mt-3 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                    {guide.bio}
                  </p>
                )}

                {/* Actions */}
                <div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
                  <Link
                    to={`/guides/${guide.id}`}
                    className="btn-ghost btn-sm"
                  >
                    <Edit className="h-4 w-4" />
                    Upravit
                  </Link>
                  <button className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
